import React, { useEffect, useState } from 'react';
import { Box, Typography, FormGroup, FormControlLabel, Checkbox, CircularProgress, Alert } from '@mui/material';
import * as ownerService from '../../services/ownerService';
import type { Feature, Owner } from '../../services/ownerService';

interface OwnerFeatureManagementPanelProps {
  owner: Owner;
}

const OwnerFeatureManagementPanel: React.FC<OwnerFeatureManagementPanelProps> = ({ owner }) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [assignedFeatures, setAssignedFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        setLoading(true);
        const [allFeatures, ownerFeatures] = await Promise.all([ownerService.getAllFeatures(), ownerService.getOwnerFeatures(owner.id)]);
        setFeatures(allFeatures);
        setAssignedFeatures(ownerFeatures.map((f: Feature) => f.id));
        setError(null);
      } catch (err) {
        setError('Failed to load feature data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, [owner.id]);

  const handleFeatureToggle = async (featureId: string, isAssigned: boolean) => {
    try {
      if (isAssigned) {
        await ownerService.removeFeatureFromOwner(owner.id, featureId);
        setAssignedFeatures(assignedFeatures.filter(id => id !== featureId));
      } else {
        await ownerService.assignFeatureToOwner(owner.id, featureId);
        setAssignedFeatures([...assignedFeatures, featureId]);
      }
    } catch (err) {
      setError('Failed to update feature.');
      console.error(err);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity='error'>{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant='h6'>Feature Flags</Typography>
      <FormGroup>
        {features.map(feature => {
          const isAssigned = assignedFeatures.includes(feature.id);
          return (
            <FormControlLabel
              key={feature.id}
              control={<Checkbox checked={isAssigned} onChange={() => handleFeatureToggle(feature.id, isAssigned)} />}
              label={`${feature.name} - ${feature.description}`}
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};

export default OwnerFeatureManagementPanel;
